<template>
  <v-dialog v-model="showDialog" max-width="600px" @update:model-value="onDialogClose">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Add Subscription</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <!-- SIM Info Display (readonly) -->
        <div class="mb-4">
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">IMSI:</span>
                <span class="detail-value">{{ simDetails.imsi || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Serial:</span>
                <span class="detail-value">{{ simDetails.serial || '-' }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">MSISDN:</span>
                <span class="detail-value">{{ simDetails.msisdn || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Client:</span>
                <span class="detail-value">{{ simDetails.client_name || '-' }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Service Select -->
        <v-select
          v-model="selectedServiceId"
          :items="availableServices"
          :item-title="formatServiceTitle"
          item-value="id"
          label="Select Subscription Service"
          :loading="loadingServices"
          :disabled="loadingServices || submitting"
          return-object
          @update:model-value="onServiceSelected"
        >
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props">
              <template v-slot:subtitle>
                {{ item.raw.short_description || '' }} |
                {{ item.raw.billing_type || '' }} |
                {{ item.raw.billing_period || '' }} |
                {{ item.raw.price || 0 }} {{ item.raw.currency || 'EUR' }}
              </template>
            </v-list-item>
          </template>
        </v-select>

        <!-- Selected Service Details -->
        <v-card v-if="selectedService" variant="outlined" class="my-3 pa-3">
          <div class="text-subtitle-2">Selected Service Details</div>
          <div class="text-body-2 mt-2">
            <strong>{{ selectedService.public_name || selectedService.name }}</strong>
          </div>
          <div class="text-body-2" v-if="selectedService.short_description">
            {{ selectedService.short_description }}
          </div>
          <div class="text-body-2 mt-1">
            <span class="text-grey">Price:</span> {{ selectedService.price }} {{ selectedService.currency || 'EUR' }} |
            <span class="text-grey">Billing:</span> {{ selectedService.billing_type }} / {{ selectedService.billing_period }} |
            <span class="text-grey">Validity:</span> {{ selectedService.validity || 30 }} days
          </div>
        </v-card>

        <v-divider class="my-3"></v-divider>

        <!-- Autorenew Toggle -->
        <v-switch
          v-model="autorenew"
          label="Auto-renew subscription"
          color="primary"
          density="compact"
          hide-details
          class="mb-3"
        ></v-switch>

        <!-- Optional Fields -->
        <v-text-field
          v-model="seriaEchipament"
          label="Seria Echipament"
          density="compact"
          variant="outlined"
          class="mb-2"
        ></v-text-field>

        <v-text-field
          v-model="nui"
          label="NUI"
          density="compact"
          variant="outlined"
          class="mb-2"
        ></v-text-field>

        <v-text-field
          v-model="technician"
          label="Technician"
          density="compact"
          variant="outlined"
        ></v-text-field>

        <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="successMessage" type="success" density="compact" class="mt-3">
          {{ successMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="outlined" @click="closeDialog" :disabled="submitting">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="submitSubscription"
          :loading="submitting"
          :disabled="!selectedServiceId || loadingServices"
        >
          Add Subscription
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getAvailableSubscriptionServices, addImsiSubscription } from "@/composables/useSubscriptionAPI";
import store from "@/store";

export default defineComponent({
  name: 'AddSubscriptionDialog',
  props: {
    simId: {
      type: [Number, String],
      required: true
    },
    simDetails: {
      type: Object,
      required: true,
      default: () => ({
        imsi: '',
        serial: '',
        msisdn: '',
        client_name: ''
      })
    }
  },
  emits: ['close', 'subscriptionAdded'],
  data() {
    return {
      showDialog: true,
      loading: false,
      loadingServices: false,
      submitting: false,
      availableServices: [],
      selectedServiceId: null,
      selectedService: null,
      autorenew: false,
      seriaEchipament: '',
      nui: '',
      technician: '',
      errorMessage: null,
      successMessage: null
    }
  },
  methods: {
    async loadAvailableServices() {
      this.loadingServices = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken
        // Pass simId to get services available to the SIM's owner client
        const response = await getAvailableSubscriptionServices(token, this.simId)

        if (!response.error) {
          this.availableServices = response.services || []
          log("Loaded available services for SIM " + this.simId + ":")
          log(this.availableServices)
          if (response.target_client) {
            log("Target client: " + response.target_client.name)
          }

          if (this.availableServices.length === 0) {
            this.errorMessage = "No subscription services available for this SIM's client"
          }
        } else {
          this.errorMessage = response.message || "Error loading services"
        }
      } catch (err) {
        log("Exception loading services:")
        log(err)
        this.errorMessage = "Error loading services"
      } finally {
        this.loadingServices = false
      }
    },

    formatServiceTitle(service) {
      if (!service) return ''
      return `${service.public_name || service.name} - ${service.price} ${service.currency || 'EUR'}`
    },

    onServiceSelected(service) {
      this.selectedService = service
      this.selectedServiceId = service ? service.id : null
    },

    async submitSubscription() {
      if (!this.selectedServiceId) {
        this.errorMessage = "Please select a service"
        return
      }

      this.submitting = true
      this.errorMessage = null
      this.successMessage = null

      try {
        const token = store.getters.getToken
        const data = {
          sim_id: this.simId,
          service_id: this.selectedServiceId,
          autorenew: this.autorenew,
          seria_echipament: this.seriaEchipament || null,
          nui: this.nui || null,
          technician: this.technician || null
        }

        log("Submitting subscription:")
        log(data)

        const response = await addImsiSubscription(token, data)

        if (!response.error) {
          if (response.payment_required && response.bt_form_url) {
            // Prepaid flow - redirect to payment
            this.successMessage = "Subscription created. Redirecting to payment..."
            log("Payment URL: " + response.bt_form_url)

            // Open payment in new window
            setTimeout(() => {
              window.open(response.bt_form_url, '_blank')
              this.$emit('subscriptionAdded', response)
              this.closeDialog()
            }, 1500)
          } else {
            // Postpaid flow - success
            this.successMessage = response.message || "Subscription created successfully!"
            this.$emit('subscriptionAdded', response)

            // Close after brief delay
            setTimeout(() => {
              this.closeDialog()
            }, 2000)
          }
        } else {
          this.errorMessage = response.message || "Error creating subscription"
        }
      } catch (err) {
        log("Exception creating subscription:")
        log(err)
        this.errorMessage = "Error creating subscription"
      } finally {
        this.submitting = false
      }
    },

    closeDialog() {
      this.showDialog = false
      this.$emit('close')
    },

    onDialogClose(value) {
      if (!value) {
        this.$emit('close')
      }
    }
  },

  mounted() {
    this.loadAvailableServices()
  }
})
</script>

<style scoped>
.detail-item {
  margin-bottom: 4px;
}

.detail-label {
  font-weight: 500;
  color: #666;
  display: block;
  font-size: 0.85rem;
}

.detail-value {
  font-size: 0.95rem;
}
</style>
